import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
} from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { parsedCurrentSheetAtom } from './atoms'
import { Column } from 'shared/ui/Table/types'
import { ParsedCrosswalkData } from './types'
import { camelToTitle } from 'components/Helpers'
import { Table } from 'shared/ui/Table/Table'
import { ConfirmWithDialogButton } from 'shared/ui'
import {
  useAlignmentHasMultiple,
  useCreateAlignments,
  useCurrentCrosswalk,
} from './hooks'

export const ParsedData = () => {
  const parsedData = useAtomValue(parsedCurrentSheetAtom)
  const hasMultiple = useAlignmentHasMultiple()
  const columns = useParsedColumns()

  if (!parsedData) return null

  return (
    <Accordion>
      <AccordionSummary>Generated Crosswalk: View alignments</AccordionSummary>
      <AccordionDetails>
        {!!parsedData.parsed.length && (
          <Table
            getRowStyle={(idx) => {
              const data = parsedData.parsed[idx]
              if (!data.sourceStandardCode || !data.targetStandardCode) {
                return {
                  backgroundColor: '#fff3cd',
                }
              }
              if (hasMultiple(data)) {
                return {
                  backgroundColor: '#ff2400',
                }
              }
            }}
            columns={columns}
            items={parsedData.parsed}
            canExport={true}
            toolBarItems={[<AlignmentsCreationButton key="alignments" />]}
          />
        )}
        {!parsedData.parsed.length && <span>Unable to parse</span>}
      </AccordionDetails>
    </Accordion>
  )
}

function AlignmentsCreationButton() {
  const parsedData = useAtomValue(parsedCurrentSheetAtom)
  const { currentCrosswalk } = useCurrentCrosswalk()
  const { canCreate, doCreate, numberCreating } = useCreateAlignments()

  if (!parsedData) return null

  const totalValidRows = parsedData.parsed.filter(
    (d) => d.sourceStandardCode && d.targetStandardCode,
  ).length
  const duplicateCount = totalValidRows - numberCreating

  return (
    <ConfirmWithDialogButton
      disabled={!canCreate}
      dialogTitle={`Create ${numberCreating} alignments`}
      dialogContent={
        <Box>
          <Box>{`Create Alignments for Crosswalk: ${currentCrosswalk?.name}`}</Box>
          {duplicateCount > 0 && (
            <Box>{`${duplicateCount} duplicate rows will be ignored.`}</Box>
          )}
          <Box>Existing Alignments will be deleted.</Box>
          <Box>Are you sure?</Box>
        </Box>
      }
      onClick={doCreate}
    >
      Create Alignments
    </ConfirmWithDialogButton>
  )
}

function useParsedColumns(): Column<ParsedCrosswalkData>[] {
  const atomData = useAtomValue(parsedCurrentSheetAtom)
  if (!atomData) return []
  const {
    rawData: { data: rawData, mapping },
  } = atomData
  return ['sourceStandardCode', 'targetStandardCode', 'rating', 'note'].map(
    (title, _, headers) => {
      return {
        title: camelToTitle(title),
        rowContent(param: ParsedCrosswalkData, { colIndex }) {
          const parsedData = param[title as keyof ParsedCrosswalkData]
          const mappedCol = mapping[headers[colIndex] as keyof typeof mapping]
          const { srcIdx } = param
          const sourceData = rawData?.[srcIdx][mappedCol] ?? ''
          return (
            <Tooltip title={sourceData} disableHoverListener={!sourceData}>
              <span>{parsedData || 'NOT_FOUND'}</span>
            </Tooltip>
          )
        },
      }
    },
  )
}
